import React, { useEffect, useState, useCallback, useRef } from "react";
import { fetchLeagues, fetchMyLeagues, createLeague, fetchGames, http, generateLeagueBanner, createCheckout, createSubscription } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GameSelect } from "./GameSelect";
import { GameMeta } from "./GameMeta";
import { isProfileComplete } from "../lib/profile";

const slugify = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").replace(/-+/g, "-");

export function HostLeaguesSection({ onToast, onRequireAuth, onRequireProfile, openCreateOnMount, onOpenLeague }) {
  const { user } = useAuth();
  const [mine, setMine] = useState([]);
  const [mineLoading, setMineLoading] = useState(true);
  const [games, setGames] = useState([]);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [slugStatus, setSlugStatus] = useState({ checking: false, available: null, reason: "" });
  const [slugEdited, setSlugEdited] = useState(false);
  const slugDebounce = useRef(null);
  // PRO LEAGUE entitlement — $9.99 tier sets plan='elite' (see backend/routes/billing.py).
  const isLeaguePro = ((user?.plan || "").toLowerCase() === "elite") || !!user?.league_pro_override;
  const startProLeagueCheckout = async () => {
    if (!user) { onRequireAuth?.(); return; }
    setCheckoutBusy(true);
    try {
      const res = await createSubscription("pro_league", window.location.origin);
      if (res?.url) window.location.href = res.url;
      else onToast?.("Couldn't start PRO LEAGUE checkout — retry.");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Checkout failed.");
    } finally { setCheckoutBusy(false); }
  };
  const [form, setForm] = useState({
    name: "",
    slug: "",
    game: "League of Legends",
    skill_min: 0,
    skill_max: 3000,
    spots_total: 8,
    league_type: "Casual",
    description: "",
    pro_badge: "",
    banner_url: "",
    pro_perks_text: "",
    tier: 1,
  });

  useEffect(() => { fetchGames().then(setGames); }, []);

  const [bannerBusy, setBannerBusy] = useState(false);

  const onBannerFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3_000_000) { onToast?.("Banner too large (max 3 MB)."); return; }
    const r = new FileReader();
    r.onload = () => setForm((p) => ({ ...p, banner_url: r.result }));
    r.readAsDataURL(f);
  };

  const onGenerateBanner = async () => {
    if (!form.name.trim()) { onToast?.("Name your league first."); return; }
    setBannerBusy(true);
    try {
      const r = await generateLeagueBanner({ name: form.name, game: form.game });
      setForm((p) => ({ ...p, banner_url: r.banner_url }));
      onToast?.("🪄 AI banner generated.");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't generate banner.");
    } finally { setBannerBusy(false); }
  };

  const load = useCallback(async () => {
    if (!user) {
      setMine([]);
      setMineLoading(false);
      return;
    }
    setMineLoading(true);
    try {
      // Use /api/leagues/mine — direct owner query, no client-side
      // filter, no risk of "No leagues yet" flashing before the
      // unfiltered list resolves (Issue #13).
      const data = await fetchMyLeagues();
      setMine(Array.isArray(data) ? data.filter((l) => l.owner_user_id === user.user_id) : []);
    } catch {
      setMine([]);
    } finally {
      // Hold the skeleton for at least 200ms so it's always
      // visible to the user instead of flashing through fast-cached
      // responses (and so the testing agent can observe it).
      setTimeout(() => setMineLoading(false), 200);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const lastConsumedTriggerRef = useRef(null);
  useEffect(() => {
    if (!openCreateOnMount || !user) return;
    if (lastConsumedTriggerRef.current === openCreateOnMount) return;
    lastConsumedTriggerRef.current = openCreateOnMount;
    if (!isProfileComplete(user)) {
      onRequireProfile?.();
    } else {
      setOpen(true);
    }
  }, [openCreateOnMount, user, onRequireProfile]);

  // Auto-fill slug from name unless user has manually edited
  useEffect(() => {
    if (!slugEdited) {
      setForm((f) => ({ ...f, slug: slugify(f.name) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name]);

  // Live availability check
  useEffect(() => {
    if (!form.slug) {
      setSlugStatus({ checking: false, available: null, reason: "" });
      return;
    }
    setSlugStatus((s) => ({ ...s, checking: true }));
    clearTimeout(slugDebounce.current);
    slugDebounce.current = setTimeout(async () => {
      try {
        const r = await http.get(`/leagues/check-slug?slug=${encodeURIComponent(form.slug)}`);
        setSlugStatus({ checking: false, available: r.data.available, reason: r.data.reason || "" });
      } catch {
        setSlugStatus({ checking: false, available: null, reason: "Check failed" });
      }
    }, 350);
    return () => clearTimeout(slugDebounce.current);
  }, [form.slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { onRequireAuth(); return; }
    if (!form.name.trim()) { onToast("Give your league a name first."); return; }
    if (!form.slug) { onToast("Pick a handle (leaguehub.co/...)"); return; }
    if (slugStatus.available === false) { onToast("Handle is taken or invalid."); return; }
    if (Number(form.skill_min) > Number(form.skill_max)) {
      onToast("MMR min must be ≤ MMR max."); return;
    }
    if (Number(form.spots_total) < 2) {
      onToast("Need at least 2 spots."); return;
    }
    if (form.league_type === "PRO" && !isLeaguePro) {
      onToast("PRO LEAGUE requires the $9.99/mo plan — upgrading now.");
      await startProLeagueCheckout();
      return;
    }
    setSubmitting(true);
    try {
      const pro_perks = form.league_type === "PRO"
        ? form.pro_perks_text.split("\n").map(s => s.trim()).filter(Boolean).slice(0, 12)
        : [];
      const created = await createLeague({
        name: form.name,
        slug: form.slug,
        game: form.game,
        skill_min: Number(form.skill_min),
        skill_max: Number(form.skill_max),
        spots_total: Number(form.spots_total),
        league_type: form.league_type,
        description: form.description,
        tier: Number(form.tier) || 1,
        pro_perks,
        pro_badge: form.league_type === "PRO" ? form.pro_badge : "",
        banner_url: form.league_type === "PRO" ? form.banner_url : "",
      });
      onToast(`🚀 ${created.name} is live at leaguehub.co/${created.slug}`);
      setOpen(false);
      setSlugEdited(false);
      setForm({
        name: "", slug: "", game: form.game, skill_min: 0, skill_max: 3000,
        spots_total: 8, league_type: "Casual", description: "",
        pro_badge: "", banner_url: "", pro_perks_text: "", tier: 1,
      });
      await load();
      setTimeout(() => {
        document.getElementById("my-hosted")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    } catch (err) {
      const status = err?.response?.status;
      // Server-side paywall — push the user into Stripe Checkout immediately.
      if (status === 402 && form.league_type === "PRO") {
        onToast?.("🔒 PRO LEAGUE locked — sending you to checkout…");
        await startProLeagueCheckout();
        return;
      }
      const msg = err?.response?.data?.detail
        || (status === 401 ? "Session expired. Sign in again." : "League creation failed.");
      onToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="leagues"
      className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-20 page-z border-t border-[#14b8a6]/20"
    >
      <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
        <div>
          <span className="px-4 py-1.5 text-[10px] font-bold tracking-[3px] bg-[#14b8a6]/10 text-[#14b8a6] rounded-full neon-border-[#14b8a6]">
            HOST LEAGUES
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display neon-text mt-5">BUILD YOUR ARENA</h2>
          <p className="text-lg text-[#a3a3ac] mt-3 max-w-xl">
            Run your own league. Set the rules, the skill band, and the spots. We handle matchmaking.
          </p>
        </div>
        <button
          onClick={() => {
            if (!user) { onRequireAuth(); return; }
            if (!isProfileComplete(user)) {
              onToast?.("Finish setting up your operator profile first.");
              onRequireProfile?.();
              return;
            }
            setOpen(true);
          }}
          data-testid="create-league-btn"
          className="btn-neon px-7 py-4 rounded-3xl flex items-center gap-3"
        >
          <i className="fas fa-plus" />
          CREATE LEAGUE
        </button>
      </div>

      <div id="my-hosted">
        {!user ? (
          <div className="glass rounded-3xl p-10 text-center">
            <p className="text-xl text-white/70">Sign in to host your own leagues.</p>
          </div>
        ) : mineLoading ? (
          <div
            className="grid md:grid-cols-2 xl:grid-cols-3 gap-6"
            data-testid="hosted-leagues-skeleton"
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="glass rounded-3xl p-6 animate-pulse"
                aria-hidden="true"
              >
                <div className="h-6 w-40 bg-white/10 rounded mb-3" />
                <div className="h-3 w-24 bg-white/10 rounded mb-2" />
                <div className="h-3 w-32 bg-white/5 rounded mb-4" />
                <div className="h-10 w-full bg-white/5 rounded" />
              </div>
            ))}
          </div>
        ) : mine.length === 0 ? (
          <div className="glass rounded-3xl p-10 text-center">
            <p className="text-xl text-white/70">No leagues yet. Hit CREATE LEAGUE to launch your first arena.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="hosted-leagues-grid">
            {mine.map((l) => {
              const isPro = l.league_type === "PRO";
              return (
              <div
                key={l.league_id}
                data-testid={`hosted-league-${l.league_id}`}
                onClick={() => onOpenLeague?.(l.league_id)}
                className={`card-3d glass rounded-3xl overflow-hidden cursor-pointer transition-colors ${
                  isPro
                    ? "border-2 border-[#14b8a6]/70 hover:border-[#14b8a6] shadow-[0_0_24px_rgba(250,204,21,0.35)]"
                    : "hover:border-[#14b8a6]/60"
                }`}
              >
                {isPro && l.banner_url && (
                  <img
                    src={l.banner_url}
                    alt=""
                    data-testid={`hosted-banner-${l.league_id}`}
                    className="w-full h-28 object-cover border-b border-[#14b8a6]/30"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                )}
                <div className="p-6">
                <div className="flex justify-between items-start mb-3">
                  <div className="min-w-0">
                    <h3 className="text-2xl font-display grad-text flex items-center gap-2 truncate">
                      {isPro && (
                        <span data-testid={`hosted-pro-badge-${l.league_id}`} className="text-[#14b8a6] text-2xl">
                          {l.pro_badge || "👑"}
                        </span>
                      )}
                      {l.name}
                    </h3>
                  </div>
                  <span className={`text-[10px] font-bold tracking-widest px-3 py-1 rounded-full whitespace-nowrap ${
                    isPro
                      ? "bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40"
                      : "bg-[#14b8a6]/20 text-[#14b8a6]"
                  }`}>
                    {isPro ? "👑 PRO" : "HOSTING"}
                  </span>
                </div>
                {l.slug && (
                  <p className="text-xs text-[#14b8a6]/90 font-bold tracking-wider mb-2">
                    <i className="fas fa-link mr-1" /> leaguehub.co/{l.slug.trim()}
                  </p>
                )}
                <div className="text-[#a3a3ac]">
                  <GameMeta gameName={l.game} games={games} size="sm" dataTestid={`hosted-game-meta-${l.league_id}`} />
                </div>
                <p className="text-sm text-white/60 mt-2 line-clamp-2">{l.description}</p>
                {isPro && l.pro_perks?.length > 0 && (
                  <ul data-testid={`hosted-perks-${l.league_id}`} className="mt-3 space-y-1">
                    {l.pro_perks.slice(0, 3).map((p, i) => (
                      <li key={i} className="text-[11px] text-[#14b8a6]/90 flex items-start gap-1.5">
                        <span className="text-[#14b8a6] leading-none mt-0.5">★</span>
                        <span className="truncate">{p}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-4 flex gap-2 flex-wrap">
                  <span className="text-xs bg-black/40 border border-white/15 rounded-2xl px-3 py-1">
                    {l.members?.length || 1}/{l.spots_total} members
                  </span>
                  <span className="text-xs bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-3 py-1">
                    MMR {l.skill_min}–{l.skill_max}
                  </span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); onOpenLeague?.(l.league_id); }}
                  data-testid={`open-hosted-${l.league_id}`}
                  className="mt-5 w-full py-3 btn-neon rounded-2xl font-bold tracking-widest"
                >
                  OPEN LEAGUE →
                </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md p-4 flex items-center justify-center"
          onClick={() => !submitting && setOpen(false)}
          data-testid="create-league-modal-backdrop"
        >
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="glass neon-glow rounded-3xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            data-testid="create-league-modal"
          >
            <h3 className="text-3xl font-display grad-text mb-6">NEW LEAGUE</h3>
            <div className="space-y-4">
              <input
                required
                placeholder="League name (e.g. Drift Racing League)"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="league-name-input"
                className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
              />
              {/* Handle / Slug */}
              <div>
                <label className="block text-xs tracking-widest text-[#14b8a6] mb-1">LEAGUE HANDLE</label>
                <div className="flex items-stretch rounded-2xl border border-[#14b8a6]/30 bg-black/40 overflow-hidden">
                  <span className="px-3 py-3 text-sm text-white/60 bg-black/50 select-none whitespace-nowrap">
                    leaguehub.co/
                  </span>
                  <input
                    required
                    value={form.slug}
                    onFocus={(e) => {
                      // First time the user touches the slug field after
                      // it was auto-generated from the name: select-all
                      // so typing replaces the auto-slug entirely
                      // instead of appending (Issue #5).
                      if (!slugEdited) e.target.select();
                    }}
                    onChange={(e) => {
                      setSlugEdited(true);
                      setForm({ ...form, slug: slugify(e.target.value) });
                    }}
                    data-testid="league-slug-input"
                    placeholder="driftleague"
                    maxLength={30}
                    className="flex-1 bg-transparent outline-none px-3 py-3 text-[#14b8a6] font-bold tracking-wide"
                  />
                  <span className="px-3 py-3 text-xs flex items-center" data-testid="slug-status">
                    {!form.slug ? (
                      <span className="text-white/30">—</span>
                    ) : slugStatus.checking ? (
                      <span className="text-[#14b8a6]">checking…</span>
                    ) : slugStatus.available === true ? (
                      <span className="text-[#14b8a6]" data-testid="slug-available">✓ AVAILABLE</span>
                    ) : slugStatus.available === false ? (
                      <span className="text-[#14b8a6]" data-testid="slug-taken">
                        {slugStatus.reason || "TAKEN"}
                      </span>
                    ) : null}
                  </span>
                </div>
                <p className="text-[10px] text-[#a3a3ac]/70 mt-1">
                  Your league lives at <span className="text-[#14b8a6] font-bold">leaguehub.co/{form.slug || "your-handle"}</span>
                </p>
              </div>
              <div>
                <label className="block text-xs tracking-widest text-[#14b8a6] mb-1">GAME</label>
                <GameSelect
                  value={form.game}
                  onChange={(g) => setForm({ ...form, game: g })}
                  games={games}
                  onGamesUpdated={setGames}
                  onToast={onToast}
                  dataTestid="league-game-select"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] tracking-[3px] text-[#14b8a6] mb-1">
                    <i className="fas fa-people-group mr-1" />NUMBER OF TEAM SLOTS
                  </label>
                  <input
                    type="number" min="2" max="100"
                    value={form.spots_total}
                    onChange={(e) => setForm({ ...form, spots_total: e.target.value })}
                    data-testid="league-spots-input"
                    className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
                  />
                  <p className="text-[10px] text-white/40 mt-1">
                    How many teams can join your league (2–100).
                  </p>
                </div>
                <div>
                  <label className="block text-[10px] tracking-[3px] text-[#14b8a6] mb-1">
                    <i className="fas fa-tag mr-1" />LEAGUE PLAN
                  </label>
                  <select
                    value={form.league_type}
                    onChange={(e) => setForm({ ...form, league_type: e.target.value })}
                    data-testid="league-type-select"
                    className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
                  >
                    <option>Casual</option>
                    <option>PRO</option>
                  </select>
                  <p className="text-[10px] text-white/40 mt-1">
                    PRO unlocks gold trim, banner upload, custom badge.
                    {!isLeaguePro && " Requires $9.99/mo PRO LEAGUE."}
                  </p>
                </div>
              </div>

              {/* PRO LEAGUE paywall — surfaced inline so users see the gate
                  BEFORE they fill out the form, and the CTA fires Stripe Checkout. */}
              {form.league_type === "PRO" && !isLeaguePro && (
                <ProLeaguePaywall onUpgrade={startProLeagueCheckout} busy={checkoutBusy} />
              )}

              <TierPicker
                tier={Number(form.tier) || 1}
                userElo={user?.elo || 0}
                onPick={(t) => setForm({ ...form, tier: t })}
              />
              <textarea
                rows="3" placeholder="Description (rules, schedule, vibe...)"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="league-desc-input"
                className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-2xl px-4 py-3 text-white"
              />
              {/* PRO premium fields appear only when PRO is selected */}
              {form.league_type === "PRO" && (
                <div className="rounded-2xl p-4 border-2 border-[#14b8a6]/60 bg-[#14b8a6]/5 space-y-3" data-testid="pro-premium-fields">
                  <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">👑 PRO LEAGUE PERKS</p>
                  <div className="flex gap-3">
                    <input
                      placeholder="🛡 Badge (1-2 chars / emoji)"
                      maxLength={4}
                      value={form.pro_badge}
                      onChange={(e) => setForm({ ...form, pro_badge: e.target.value })}
                      data-testid="league-pro-badge-input"
                      className="w-32 bg-black/40 border border-[#14b8a6]/40 rounded-2xl px-4 py-3 text-[#14b8a6] font-bold text-center"
                    />
                    <input
                      placeholder="Brand banner URL (or leave blank for AI)"
                      value={form.banner_url?.startsWith("data:") ? "" : form.banner_url}
                      onChange={(e) => setForm({ ...form, banner_url: e.target.value })}
                      data-testid="league-banner-input"
                      className="flex-1 bg-black/40 border border-[#14b8a6]/40 rounded-2xl px-4 py-3 text-white"
                    />
                  </div>

                  {/* Upload or AI-generate */}
                  <div className="flex flex-wrap items-center gap-2">
                    <label
                      data-testid="league-banner-upload"
                      className="cursor-pointer text-[11px] tracking-widest px-3 py-2 rounded-xl bg-black/40 border border-[#14b8a6]/40 text-[#14b8a6] hover:bg-[#14b8a6]/10"
                    >
                      <i className="fas fa-upload mr-1.5" />UPLOAD IMAGE
                      <input type="file" accept="image/*" onChange={onBannerFile} className="hidden" />
                    </label>
                    <button
                      type="button"
                      onClick={onGenerateBanner}
                      disabled={bannerBusy}
                      data-testid="league-banner-ai-btn"
                      className="text-[11px] tracking-widest px-3 py-2 rounded-xl bg-gradient-to-r from-[#14b8a6] to-[#14b8a6] text-white font-bold shadow-[0_0_18px_rgba(20, 184, 166,0.45)] hover:scale-[1.02] transition disabled:opacity-60"
                    >
                      {bannerBusy
                        ? <><i className="fas fa-spinner fa-spin mr-1.5" />GENERATING...</>
                        : <><i className="fas fa-wand-magic-sparkles mr-1.5" />🪄 GENERATE WITH AI</>}
                    </button>
                    {form.banner_url && (
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, banner_url: "" })}
                        data-testid="league-banner-clear"
                        className="text-[10px] tracking-widest px-2.5 py-2 rounded-xl btn-ghost"
                      >
                        <i className="fas fa-times mr-1" />CLEAR
                      </button>
                    )}
                    <p className="text-[10px] text-[#14b8a6]/65 ml-auto">
                      Leave blank to auto-generate on submit.
                    </p>
                  </div>

                  {form.banner_url && (
                    <div
                      data-testid="league-banner-preview"
                      className="h-24 rounded-2xl border border-[#14b8a6]/40 bg-black/40"
                      style={{
                        backgroundImage: `url(${form.banner_url})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    />
                  )}
                  <textarea
                    rows="3"
                    placeholder="Perks — one per line (e.g. 'Custom team badges', 'Weekly cash pot', 'Coach review sessions')"
                    value={form.pro_perks_text}
                    onChange={(e) => setForm({ ...form, pro_perks_text: e.target.value })}
                    data-testid="league-pro-perks-input"
                    className="w-full bg-black/40 border border-[#14b8a6]/40 rounded-2xl px-4 py-3 text-white text-sm"
                  />
                  <p className="text-[10px] text-[#14b8a6]/60">
                    💡 PRO leagues get the gold-trim look, custom badge on every card, and one-click invite links.
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={submitting}
                data-testid="league-cancel-btn"
                className="btn-ghost px-5 py-3 rounded-2xl disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || checkoutBusy}
                data-testid="league-submit-btn"
                className="btn-neon px-7 py-3 rounded-2xl disabled:opacity-60"
              >
                {submitting ? "LAUNCHING..."
                  : (form.league_type === "PRO" && !isLeaguePro)
                    ? "🔒 UNLOCK PRO LEAGUE ($9.99/mo)"
                    : "🚀 LAUNCH"}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

export default HostLeaguesSection;

function ProLeaguePaywall({ onUpgrade, busy }) {
  return (
    <div
      data-testid="pro-league-paywall"
      className="rounded-2xl p-4 border-2 border-[#14b8a6]/70 bg-gradient-to-br from-[#14b8a6]/10 via-[#14b8a6]/10 to-[#14b8a6]/5"
    >
      <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold">
        <i className="fas fa-lock mr-1" />PRO LEAGUE LOCKED · $9.99 / MO
      </p>
      <p className="text-base font-display mt-2 leading-tight">
        Host with the gold trim. AI banners, custom badges, prize pools, &amp; the
        invite-only PRO crest on every match.
      </p>
      <ul className="mt-3 text-[12px] text-white/80 space-y-1 list-none">
        <li>👑 PRO crest + gold neon trim on your league card</li>
        <li>🎨 AI-generated cinematic banner (or upload your own)</li>
        <li>🛡 Custom badge slot (1-2 chars / emoji)</li>
        <li>🏆 One-shot redeem invites that bypass applications</li>
        <li>🏰 Invite entire teams to your league in one click</li>
        <li>💬 League-to-league DMs to coordinate inter-league cups</li>
      </ul>
      <button
        type="button"
        onClick={onUpgrade}
        disabled={busy}
        data-testid="pro-league-paywall-upgrade"
        className="mt-4 w-full rounded-2xl bg-[#14b8a6] text-black font-bold py-3 text-xs tracking-widest hover:brightness-110 disabled:opacity-60"
      >
        {busy ? "OPENING CHECKOUT…" : "🔓 UPGRADE TO PRO LEAGUE — $9.99/MO"}
      </button>
      <p className="text-[10px] text-white/50 mt-2 text-center">
        Or pick <b>Casual</b> above — free forever, all core features included.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TierPicker — gamer-flavoured tier selector with MMR mismatch warning.
// ---------------------------------------------------------------------------
const TIERS = [
  {
    code: 1, label: "SPAWN ZONE", emoji: "🟢",
    blurb: "Just starting out.",
    minElo: 0,
    border: "border-[#14b8a6]/50", accent: "text-[#14b8a6]",
    glow: "shadow-[0_0_18px_rgba(20, 184, 166,0.35)]",
    bg: "bg-[#14b8a6]/10",
  },
  {
    code: 2, label: "WAR ROOM", emoji: "⚔",
    blurb: "Seasoned operators.",
    minElo: 1400,
    border: "border-[#14b8a6]/60", accent: "text-[#14b8a6]",
    glow: "shadow-[0_0_22px_rgba(20, 184, 166,0.45)]",
    bg: "bg-[#14b8a6]/10",
  },
  {
    code: 3, label: "GOD MODE", emoji: "👑",
    blurb: "Elite only.",
    minElo: 1800,
    border: "border-[#14b8a6]/70", accent: "text-[#14b8a6]",
    glow: "shadow-[0_0_24px_rgba(253,224,71,0.55)]",
    bg: "bg-[#14b8a6]/10",
  },
];

function TierPicker({ tier, userElo, onPick }) {
  const picked = TIERS.find((t) => t.code === tier) || TIERS[0];
  const eloMismatch = userElo > 0 && userElo < picked.minElo;
  const suggested = [...TIERS].reverse().find((t) => userElo >= t.minElo) || TIERS[0];

  return (
    <div data-testid="league-tier-picker" className="space-y-2">
      <label className="block text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
        <i className="fas fa-bolt mr-1" />LEAGUE TIER
      </label>
      <div className="grid grid-cols-3 gap-2">
        {TIERS.map((t) => {
          const active = tier === t.code;
          return (
            <button
              key={t.code}
              type="button"
              onClick={() => onPick(t.code)}
              data-testid={`league-tier-${t.code}`}
              aria-pressed={active}
              className={`rounded-2xl p-3 text-left border transition ${
                active
                  ? `${t.border} ${t.bg} ${t.glow}`
                  : "border-white/15 bg-black/40 hover:border-white/30"
              }`}
            >
              <div className={`text-[10px] tracking-[3px] font-bold ${active ? t.accent : "text-white/70"}`}>
                T{t.code} · {t.emoji} {t.label}
              </div>
              <p className={`mt-1 text-[10px] leading-tight ${active ? "text-white/85" : "text-white/50"}`}>
                {t.blurb}
              </p>
              <p className={`mt-1 text-[9px] tracking-wider ${active ? t.accent : "text-white/40"}`}>
                {t.minElo === 0 ? "ANY MMR" : `${t.minElo}+ MMR`}
              </p>
            </button>
          );
        })}
      </div>
      {eloMismatch && (
        <div
          data-testid="league-tier-warning"
          className="flex items-start gap-2 rounded-xl border border-[#14b8a6]/40 bg-[#14b8a6]/5 p-2.5 text-[11px] text-[#14b8a6]"
        >
          <i className="fas fa-triangle-exclamation mt-0.5 text-[#14b8a6]" />
          <span>
            Your MMR is <span className="font-bold">{userElo}</span> — below {picked.label}'s {picked.minElo}+
            threshold. Most operators at your level start in{" "}
            <button
              type="button"
              onClick={() => onPick(suggested.code)}
              data-testid="league-tier-suggested-btn"
              className="underline font-bold text-[#14b8a6] hover:text-[#14b8a6]"
            >
              {suggested.emoji} {suggested.label}
            </button>
            {" "}— want to drop in there?
          </span>
        </div>
      )}
    </div>
  );
}
